/* ═════════════════════════════════════════════════════════════════════════════
   SRC/COMPONENTS/SEGSECTION/DATATRANSFORMS.JS — funções puras de dados
   ─────────────────────────────────────────────────────────────────────────────
   O QUE FAZ
   Cálculos e agrupamentos (sem React, sem Redux, sem rede) usados por
   SegSection (./index.jsx) para colorir por valor, montar os cards de
   delta (último vs primeiro ponto da série), classificar numa faixa da
   ESCALA e ordenar a lista de cidades. Extraídas do componente pra poderem
   ser testadas isoladamente — mesmo padrão de tabTrendsView/dataTransforms.js
   e tabSegmentationView/dataTransforms.js.

   USADO POR
   ./index.jsx (SegSection).
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Cor do indicador em função do valor. NPS tem faixas próprias (−100 a 100);
 * os demais indicadores (CSAT/CES, 0 a 100%) usam faixas mais altas.
 * `colors` injeta os tokens de cor pra manter esse módulo livre de
 * dependência do design system.
 */
export function corPorValor(label, v, colors) {
  if (label === "NPS") {
    return v >= 75
      ? colors.green
      : v >= 50
        ? colors.g700
        : v >= 0
          ? colors.warn
          : colors.red;
  }
  return v >= 80
    ? colors.green
    : v >= 65
      ? colors.g700
      : v >= 50
        ? colors.warn
        : colors.red;
}

/**
 * Último e primeiro valor de `key` na série `segData`, e o delta entre eles
 * (usado nos cards "Titular vs Dependente" / "Individual vs Empresa").
 * Série vazia ou sem o ponto não lança — cai em 0.
 */
export function computeStatDelta(segData, key) {
  const last =
    (segData[segData.length - 1] ? segData[segData.length - 1][key] : 0) || 0;
  const first = (segData[0] ? segData[0][key] : 0) || 0;
  return { last, first, delta: last - first };
}

/**
 * Faixa da ESCALA (array ordenado de min decrescente) em que `value` se
 * encaixa. Cai na última faixa (a mais baixa) quando nada bate — mesmo
 * fallback usado em todos os pontos do componente que leem a ESCALA.
 */
export function statusForValue(value, escala) {
  return escala.find((e) => value >= e.min) || escala[escala.length - 1];
}

/**
 * Cidades ordenadas por `cidadeKey` decrescente. Cidade sem valor pro
 * indicador (`null`/`undefined`) vai pro fim da lista em vez de quebrar a
 * ordenação ou aparecer no topo por acidente de comparação com `undefined`.
 */
export function sortCidadesByKey(cidadesData, cidadeKey) {
  return [...cidadesData].sort(
    (a, b) => (b[cidadeKey] ?? -Infinity) - (a[cidadeKey] ?? -Infinity),
  );
}

/** true quando nenhum item de `items` está oculto em `hiddenMap`. */
export function allItemsVisible(items, hiddenMap, keyOf) {
  return items.every((item) => !hiddenMap[keyOf(item)]);
}

/** true quando todo item de `items` está oculto em `hiddenMap`. */
export function allItemsHidden(items, hiddenMap, keyOf) {
  return items.every((item) => hiddenMap[keyOf(item)]);
}

/** Quantos itens de `items` estão atualmente visíveis (não ocultos). */
export function visibleItemsCount(items, hiddenMap, keyOf) {
  return items.filter((item) => !hiddenMap[keyOf(item)]).length;
}
